import React, { useMemo, useState } from "react";
import styled from "styled-components";

import Modal from "../../../components/modal/Modal";
import UserListRow from "../../../components/user-item/UserListRow";
import {
  useGetAllProjectMembershipsQuery,
  type ProjectMembership,
} from "../../../app/accounts/memberships";
import {
  useCreateChatThreadMutation,
  type ChatThread,
} from "../../../app/services/chatThreads";

/**
 * DM（オファー非依存のグループ DM）の相手先選択モーダル。
 *
 * - 利用者はクライアント責任者（CLIENT）/ プロデューサー責任者（PRODUCER）。
 * - 相手先候補は作成者のサイドで変わる:
 *   - CLIENT: クライアント / レビュアー / プロデューサー / ディレクター
 *   - PRODUCER: 上記 + スペシャリスト
 *   - いずれも自分自身と管理者（ADMIN）は候補から除外。
 * - 複数選択して 1 つのグループ DM スレッドを作成する。
 *
 * ポジションは API 表記（CLIENT = BE の OWNER）。最終的な相手先検証は BE 側の
 * ChatThreadApiView._allowed_recipient_positions でも行う（FE はUX上の絞り込み）。
 */

interface CreateDmThreadModalProps {
  projectId: string;
  currentUserId?: number;
  /** 作成者のプロジェクト内ポジション（API 表記。CLIENT / PRODUCER 想定） */
  currentUserPosition: string;
  onClose: () => void;
  onCreated: (thread: ChatThread) => void;
}

// 表示順（クライアント側 → プロデューサー側 → スペシャリスト）
const POSITION_ORDER = [
  "CLIENT",
  "REVIEWER",
  "PRODUCER",
  "DIRECTOR",
  "SPECIALIST",
];

const SelectableRow = styled.div<{ $selected: boolean }>`
  cursor: pointer;
  border-radius: 4px;
  background-color: ${({ $selected }) =>
    $selected ? "var(--soremo-background)" : "transparent"};

  &:hover {
    background-color: var(--soremo-background);
  }
`;

const SelectIcon = styled.span<{ $selected: boolean }>`
  font-size: 24px;
  flex-shrink: 0;
  color: ${({ $selected }) =>
    $selected ? "var(--soremo-blue)" : "var(--soremo-light-gray)"};
`;

const EmptyState = styled.div.attrs({
  className: "bodytext-13 u-text-light-gray",
})`
  text-align: center;
  padding: 24px 8px;
`;

const CreateDmThreadModal: React.FC<CreateDmThreadModalProps> = ({
  projectId,
  currentUserId,
  currentUserPosition,
  onClose,
  onCreated,
}) => {
  const { data: membershipsData, isLoading } = useGetAllProjectMembershipsQuery(
    { projectId },
    { skip: !projectId },
  );
  const [createChatThread, { isLoading: isCreating }] =
    useCreateChatThreadMutation();
  const [selectedIds, setSelectedIds] = useState<Set<number>>(new Set());

  const candidates = useMemo<ProjectMembership[]>(() => {
    const members = membershipsData?.data ?? [];
    // プロデューサー責任者のみスペシャリストを相手に選べる。
    const allowed = new Set(
      currentUserPosition === "PRODUCER"
        ? ["CLIENT", "REVIEWER", "PRODUCER", "DIRECTOR", "SPECIALIST"]
        : ["CLIENT", "REVIEWER", "PRODUCER", "DIRECTOR"],
    );
    return members
      .filter((m) => !m.is_invited)
      .filter((m) => m.user_id !== currentUserId)
      .filter((m) => !!m.position && allowed.has(m.position))
      .slice()
      .sort(
        (a, b) =>
          POSITION_ORDER.indexOf(a.position) -
          POSITION_ORDER.indexOf(b.position),
      );
  }, [membershipsData, currentUserId, currentUserPosition]);

  const toggle = (userId: number) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(userId)) {
        next.delete(userId);
      } else {
        next.add(userId);
      }
      return next;
    });
  };

  const handleCreate = async () => {
    if (selectedIds.size === 0) return;
    try {
      const res = await createChatThread({
        project_id: projectId,
        member_user_ids: Array.from(selectedIds),
      }).unwrap();
      onCreated(res.data.thread);
    } catch (error) {
      console.warn("[CreateDmThreadModal] createChatThread failed", error);
    }
  };

  const description =
    selectedIds.size > 0
      ? `${selectedIds.size}人を選択中`
      : "メッセージを送る相手を選択してください（複数選択可）";

  return (
    <Modal
      variant="dialog"
      title="新規DM"
      description={description}
      onClose={onClose}
      primaryAction={{
        label: "作成",
        onClick: handleCreate,
        disabled: selectedIds.size === 0 || isCreating,
        loading: isCreating,
        theme: "primary",
      }}
      secondaryAction={{
        label: "キャンセル",
        onClick: onClose,
      }}
    >
      {isLoading ? (
        <EmptyState>読み込み中...</EmptyState>
      ) : candidates.length === 0 ? (
        <EmptyState>DMを送れる相手がいません</EmptyState>
      ) : (
        candidates.map((m) => {
          const selected = selectedIds.has(m.user_id);
          return (
            <SelectableRow
              key={m.user_id}
              $selected={selected}
              onClick={() => toggle(m.user_id)}
            >
              <UserListRow
                variant="flat"
                user={{
                  id: m.user_id,
                  avatar_url: m.avatar_url,
                  display_name: m.display_name_jp,
                  title: m.title_jp,
                }}
                trailingSlot={
                  <SelectIcon
                    className="material-symbols-rounded"
                    $selected={selected}
                  >
                    {selected ? "check_circle" : "radio_button_unchecked"}
                  </SelectIcon>
                }
              />
            </SelectableRow>
          );
        })
      )}
    </Modal>
  );
};

export default CreateDmThreadModal;
